import { isReutersDotcom, isReutersPreview } from '$utils/env';

import getMatches from '$utils/getMatches';
import getTeams from '$utils/getTeams';
import { resolve } from '$app/paths';
import shouldShowKnockouts from '$utils/shouldShowKnockouts';

// `resolve('/')` returns the configured SvelteKit base path with a trailing
// slash (e.g. `'/'`, `'/my-app/'`). We strip the trailing slash so callers
// can compose URLs with their own `/path/...` suffix — replacing the
// (now-deprecated) `base` export from `$app/paths`.
const stripTrailingSlash = (p: string) =>
  p.length > 1 && p.endsWith('/') ? p.slice(0, -1)
  : p === '/' ? ''
  : p;
const basePath = stripTrailingSlash(resolve('/'));

// Global reactive app state. Build-time config (tournament type, data
// folder, etc.) lives in `src/lib/config/settings.json` — not here.

interface TeamsArray extends Array<any> {
  getTeam: (slugNameCodeOrID: string) => any | null;
  getTeamSlugById: (id: string | undefined) => string | undefined;
}

interface MatchesArray extends Array<any> {
  getMatch: (id: string | undefined) => any | null;
}

class AppState {
  // Runtime state — mutated by layouts, the data poller, resize handlers, etc.
  isLoading = $state(true);
  optaData = $state<Record<string, any>>({});
  /** Full tournament roster, derived so it auto-updates with `optaData`. */
  teams = $derived<TeamsArray>(getTeams(this.optaData?.matches));
  /** Full match list (decorated with `isoDate`), derived from `optaData`. */
  matches = $derived<MatchesArray>(getMatches(this.optaData?.matches));
  /**
   * Whether the knockout bracket UI should be revealed. Derived (not
   * `$state` updated via an effect) so it's evaluated synchronously
   * during SSR and the very first client render — preventing a
   * GroupTable→Bracket flash on slow networks.
   */
  showKnockouts = $derived<boolean>(shouldShowKnockouts(this.matches));
  liveMatch = $state<boolean | null>(null);
  windowWidth = $state(1000);
  /**
   * True when the current view should behave as an embed — i.e. inside
   * a third-party iframe or rendered as a share card. Toggled by:
   *   • `pages/(embeds)/+layout.svelte` for `/embeds/*` routes
   *   • `pages/(app)/+layout.svelte` for `/share` routes
   * Read by helpers like `getMatchPath` / `getTeamPath` to decide
   * whether to return absolute production URLs.
   */
  embed = $state(false);
  customStyles = $state<any>(null);
  /** Mirrored from SvelteKit's `page.url` in the root `(app)` layout. */
  currentUrl = $state<URL | null>(null);

  /**
   * Team in focus on the current page. Each route layout is responsible
   * for setting this (e.g. `teams/[slug]/+layout.svelte`); the root
   * `(app)` layout clears it whenever we navigate to a route that has
   * no concept of an active country.
   */
  activeCountry = $state<any[]>([]);

  /**
   * URL prefix all in-app navigation links are composed against. Equals
   * the configured SvelteKit base path (empty string when no base is
   * set). Components/utilities should build links as
   * `` `${appState.linkBase}/some/path/` ``.
   */
  get linkBase() {
    return basePath;
  }

  get isReutersDomain() {
    if (!this.currentUrl) return false;
    return (
      isReutersDotcom(this.currentUrl) || isReutersPreview(this.currentUrl)
    );
  }

  /**
   * True when the current page is being rendered inside an iframe
   * (e.g. a Pym-embedded matches widget on a third-party site).
   * Returns `false` during SSR/build since there's no `window`. Use
   * this in route layouts to decide whether to flip `embed = true`.
   */
  get isInIframe() {
    if (typeof window === 'undefined') return false;
    try {
      return window.self !== window.top;
    } catch {
      // Cross-origin frames throw on `window.top` access — that itself
      // means we're inside a third-party iframe.
      return true;
    }
  }
}

export const appState = new AppState();
